import { refs } from '../../core/refs.js';
import { escapeHtml } from '../../core/escape.js';
import { getSandboxUrl, isLocalDevelopmentOrigin } from '../../core/sandbox-origin.js';
import { markPreviewTimingById } from './timing.js';

let isSandboxReady = false;
let useSandbox = true;
let sandboxUrlOverride = null;
let cachedPayload = null;
let transportHooks = {
  onRenderPosted: null,
  onUnavailable: null
};

/**
 * Lets the run policy observe renders leaving for the sandbox (safe-boot flag)
 * and the preview becoming unavailable (no render can complete).
 */
export function configureTransportHooks(hooks = {}) {
  transportHooks = {
    ...transportHooks,
    ...hooks
  };
}

export function getActiveSandboxUrl() {
  if (!useSandbox) return null;
  return sandboxUrlOverride || getSandboxUrl();
}

export function getActiveSandboxOrigin() {
  const sandboxUrl = getActiveSandboxUrl();
  if (!sandboxUrl) return '';
  try {
    return new URL(sandboxUrl).origin;
  } catch (err) {
    return '';
  }
}

function getSameOriginSandboxUrl() {
  const parent = encodeURIComponent(window.location.origin);
  return `${window.location.origin}/sandbox.html?parent=${parent}`;
}

function buildNoticeDocument(title, detail) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    html, body { margin: 0; min-height: 100%; background: #fff8f0; color: #271813; font-family: system-ui, sans-serif; }
    body { display: grid; place-items: center; padding: 24px; box-sizing: border-box; }
    main { max-width: 520px; border: 3px solid #271813; background: #ffffff; padding: 18px; box-shadow: 6px 6px 0 #271813; }
    h1 { margin: 0 0 8px; font-size: 18px; }
    p { margin: 0; font-size: 14px; line-height: 1.45; }
  </style>
</head>
<body>
  <main>
    <h1>${escapeHtml(title)}</h1>
    <p>${escapeHtml(detail)}</p>
  </main>
</body>
</html>`;
}

export function showPreviewUnavailable(reason = '') {
  cachedPayload = null;
  isSandboxReady = false;
  transportHooks.onUnavailable?.();
  const detail = reason || 'The cross-origin preview sandbox is not available.';
  if (refs.previewIframe) {
    refs.previewIframe.removeAttribute('src');
    refs.previewIframe.srcdoc = buildNoticeDocument('Preview unavailable', detail);
  }
}

export function isActiveSandboxReady() {
  return isSandboxReady;
}

export function setSandboxReady(ready) {
  isSandboxReady = ready;
  if (ready && cachedPayload) {
    postToSandbox(cachedPayload);
    cachedPayload = null;
  }
}

export function fallbackToSameOriginPreview(reason = '') {
  if (!isLocalDevelopmentOrigin()) {
    sandboxUrlOverride = null;
    useSandbox = false;
    showPreviewUnavailable('Production preview requires a separate preview origin. Configure the cross-origin sandbox and try again.');
    if (reason) {
      console.error(`[Front-my-End] Cross-origin preview sandbox unavailable. Same-origin fallback is disabled outside local development. ${reason}`);
    }
    return false;
  }

  const fallbackUrl = getSameOriginSandboxUrl();
  sandboxUrlOverride = fallbackUrl;
  useSandbox = true;
  isSandboxReady = false;
  if (reason) {
    console.warn(`[Front-my-End] Cross-origin preview sandbox unavailable. Using local-development same-origin sandbox fallback. ${reason}`);
  }
  if (refs.previewIframe) {
    refs.previewIframe.removeAttribute('srcdoc');
    refs.previewIframe.src = fallbackUrl;
  }
  return true;
}

export function postToSandbox(payload) {
  const sandboxUrl = getActiveSandboxUrl();
  if (refs.previewIframe && refs.previewIframe.contentWindow && sandboxUrl) {
    try {
      const targetOrigin = new URL(sandboxUrl).origin;
      markPreviewTimingById(payload?.timingId, 'sandbox-message-send-start');
      refs.previewIframe.contentWindow.postMessage(payload, targetOrigin);
      markPreviewTimingById(payload?.timingId, 'sandbox-message-send-end');
      if (payload?.type === 'render') {
        transportHooks.onRenderPosted?.(payload.timingId);
      }
    } catch (e) {
      console.error('Failed to post to sandbox origin:', e);
    }
  }
}

export function queueSandboxPayload(payload) {
  markPreviewTimingById(payload?.timingId, 'sandbox-message-queued');
  cachedPayload = payload;
}

/**
 * Renders a static, script-free notice through the live sandbox without
 * tearing it down, so a later Run still reaches the same relay.
 */
export function showPreviewNotice(title, detail) {
  if (!getActiveSandboxUrl()) {
    showPreviewUnavailable(detail);
    return;
  }
  const payload = { type: 'render', content: buildNoticeDocument(title, detail) };
  if (isSandboxReady) {
    postToSandbox(payload);
  } else {
    queueSandboxPayload(payload);
  }
}

/**
 * Clears all transport state and points the preview frame back at the
 * configured sandbox, so a failed or fallen-back preview can genuinely retry.
 * Returns the sandbox URL, or null when no sandbox origin is configured.
 */
export function resetTransport() {
  isSandboxReady = false;
  useSandbox = true;
  sandboxUrlOverride = null;
  cachedPayload = null;
  const sandboxUrl = getSandboxUrl();
  if (sandboxUrl && refs.previewIframe) {
    refs.previewIframe.removeAttribute('srcdoc');
    refs.previewIframe.src = sandboxUrl;
  }
  return sandboxUrl;
}
